import { listBrowserLogs, redactBrowserLogText } from "../debug/browserLogs";
import { inspectArchiveHealth, listLlmDebugLogs, listTurnTraces } from "./db";
import { estimateStorageUsage, probeStorageResponsiveness } from "./storageHealth";

export interface DiagnosticBundleOptions { archiveName?: string; includeContent?: boolean }

function sanitizeArchiveHealth(archiveHealth: Awaited<ReturnType<typeof inspectArchiveHealth>>, includeContent: boolean) {
  return {
    ...archiveHealth,
    pendingTurns: archiveHealth.pendingTurns.map((turn) => ({
      id: turn.id,
      archiveName: turn.archiveName,
      startedAt: turn.startedAt,
      updatedAt: turn.updatedAt,
      phase: turn.phase,
      error: turn.error ? redactBrowserLogText(turn.error) : undefined,
      ...(includeContent ? { input: redactBrowserLogText(turn.input), assistantText: turn.assistantText ? redactBrowserLogText(turn.assistantText) : undefined } : {}),
    })),
  };
}

export async function buildDiagnosticBundle(options: DiagnosticBundleOptions = {}) {
  const [archiveHealth, storageHealth, storageUsage, llmLogs, turnTraces] = await Promise.all([
    inspectArchiveHealth(), probeStorageResponsiveness(), estimateStorageUsage(),
    listLlmDebugLogs(options.archiveName), listTurnTraces(options.archiveName),
  ]);
  return {
    format: "mh-rp-diagnostics-v1",
    exportedAt: new Date().toISOString(),
    app: { version: import.meta.env.VITE_APP_VERSION ?? "development", href: location.origin, userAgent: navigator.userAgent },
    archiveName: options.archiveName,
    archiveHealth: sanitizeArchiveHealth(archiveHealth, options.includeContent === true),
    storageHealth: { ...storageHealth, archive: storageHealth.archive ? sanitizeArchiveHealth(storageHealth.archive, options.includeContent === true) : undefined },
    storageUsage,
    browserLogs: listBrowserLogs().map((entry) => ({ ...entry, message: redactBrowserLogText(entry.message) })),
    llmLogs: llmLogs.map((entry) => ({
      id: entry.id, archiveName: entry.archiveName, turnId: entry.turnId, featureId: entry.featureId,
      routeId: entry.routeId, host: entry.host, model: entry.model, attempt: entry.attempt,
      status: entry.status, startedAt: entry.startedAt, durationMs: entry.durationMs,
      truncated: entry.truncated, error: entry.error ? redactBrowserLogText(entry.error) : undefined,
      usage: entry.usage,
      ...(options.includeContent ? { requestPreview: redactBrowserLogText(entry.requestPreview), responsePreview: redactBrowserLogText(entry.responsePreview) } : {}),
    })),
    turnTraces,
    privacy: { llmContentIncluded: options.includeContent === true, runtimeErrorMessagesIncluded: true, secretsRedacted: true },
  };
}

export function downloadJson(value: unknown, filename: string) {
  const url = URL.createObjectURL(new Blob([JSON.stringify(value, null, 2)], { type: "application/json" }));
  const anchor = document.createElement("a");
  anchor.href = url; anchor.download = filename; anchor.click();
  URL.revokeObjectURL(url);
}
